import { holdsAnywhere } from '@carii/casl-abilities';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router';
import { type AlternativeCar, AlternativeCars } from '../../components/ui/AlternativeCars.js';
import { StatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Card } from '../../components/ui/Card.js';
import { CustomerToldNote } from '../../components/ui/CustomerToldNote.js';
import { Input, Textarea } from '../../components/ui/FormField.js';
import { type CarFuel, FuelBars } from '../../components/ui/FuelBars.js';
import { GapWarning } from '../../components/ui/GapWarning.js';
import { Modal } from '../../components/ui/Modal.js';
import { PageHeader } from '../../components/ui/PageHeader.js';
import { useAbility } from '../../context/AbilityContext.js';
import { useSaveMutation } from '../../hooks/useSaveMutation.js';
import { api } from '../../services/api.js';
import { RESOURCE_KEYS } from '../../services/resourceKeys.js';
import { downloadReceipt } from '../../utils/downloadReceipt.js';
import { type Trip, purposeLabel } from '../../utils/tripLabels.js';
import { CarHostsLine } from '../cars/CarHostsChips.js';
import { BookingPenaltiesCard } from '../penalties/BookingPenaltiesCard.js';
import { penaltyPowers } from '../penalties/penalties.js';
import { BookingJumpLinks } from './BookingJumpLinks.js';
import { BookingSummaryLine } from './BookingSummaryLine.js';
import { ExtendRentalCard } from './ExtendRentalCard.js';
import { InspectionsSection } from './InspectionsSection.js';
import { KmChargeCard, type MileageCharge, type MileagePackage } from './KmChargeCard.js';
import { type LatePaymentBlock, LatePaymentCard } from './LatePaymentCard.js';
import { LateStatusPanel } from './LateStatusPanel.js';
import { ZoneDistanceCard, type ZoneMileage } from './ZoneDistanceCard.js';
import type { PhotoWindows } from './photoWindows.js';

interface PricingTotals {
  total_minor: number;
  amount_due_minor?: number;
  // 097: absent on a pre-097 booking (research.md R7) — never rendered as 0 in that case.
  non_refundable_minor?: number;
  // 358: credit drawn by this booking (from "Use my credit" or a credit code).
  gift_cert_credit_minor?: number;
  override_total_minor?: number;
  override_reason?: string;
  overridden_at?: string;
}

/**
 * #17: the renter, resolved by the backend for administrators only. Null when the account is
 * gone; absent entirely on a payload from a backend that predates the field.
 */
interface BookingCustomer {
  id: string;
  /** The account's name, or its email when it carries no name. */
  name: string;
  email: string;
  /** #24. Null when the account has none — most do not. */
  phone: string | null;
}

interface Booking {
  /** 053: canonical identifier. `_id` is still on the wire but deprecated. */
  id: string;
  user_id: string;
  customer?: BookingCustomer | null;
  car_id: string;
  /** 052 hydration — same summary `BookingListPage` already reads, unused here until 203. */
  car?: {
    id: string;
    label: string;
    plate_number: string;
    lockbox_code?: string | null;
    /** 408 (#1434): the car's fuel now (staff: always, with litres); absent when the car has none. */
    fuel?: CarFuel;
  } | null;
  location_id: string;
  /** 416 (#1431): the geo-fence figures (staff); null until the platform first looks at the rental. */
  zone_mileage?: ZoneMileage | null;
  /** 417: the rental's mileage package (null when none was offered) and staff's km charge. */
  mileage_package?: MileagePackage | null;
  mileage_charge?: MileageCharge | null;
  /** Admin-only (FR-005). 300 (#403): what the Log-a-refund box fetches its payment through. */
  payment_id?: string;
  status: string;
  pickup_at: string;
  return_at: string;
  base_price_minor: number;
  discount_minor: number;
  total_price_minor: number;
  currency: string;
  notes?: string;
  /** 361: the customer's trip answers; absent on older bookings and staff bookings made without them. */
  trip?: Trip;
  cancellation_reason?: string;
  created_at: string;
  pricing_snapshot?: { totals?: PricingTotals };
  // 358 (FR-026): staff only; one row per certificate the booking's credit came from.
  credit_draws?: Array<{ gift_certificate_id: string; code: string | null; amount_minor: number }>;
  /** 304: present only on a booking that is itself an extension of another booking. */
  parent_booking_id?: string;
  /** 304: present only while this booking's extension has not been cancelled. */
  child_booking_id?: string;
  /** 325 (#477): the rental this booking belongs to (the original booking and its extensions). */
  rental?: {
    /** 325: the rental's id (its original booking); 390 names the receipt file by it. */
    rental_id?: string;
    /** 374: the rental's first pickup and last return. */
    pickup_at?: string;
    return_at?: string;
    legs: Array<{
      id: string;
      status: string;
      pickup_at: string;
      return_at: string;
      total_price_minor: number;
      is_extension: boolean;
    }>;
    total_price_minor: number;
  };
  /** 304: the first booking in this chain, denormalized. Not rendered — parent/child cover
      navigation; root exists for the backend's own O(1) tail lookup, not for this screen. */
  root_booking_id?: string;
  /** 311 (#549): staff-only, present only on a cancelled booking — true while it still holds its
      car (cancelled while checked out, and nobody has marked the vehicle returned yet). */
  holds_car?: boolean;
  /** 387 (#1308): why the customer cannot see the lockbox code, for staff; and a staff release, if any. */
  lockbox_gate?: 'show' | 'await_photos' | 'ask_staff';
  lockbox_release?: { at: string; by_name: string | null; reason?: string } | null;
  /** 390 (#1310): the platform's word that this rental's receipt can be downloaded (finished rentals only). */
  receipt_available?: boolean;
  /** 410 (#1472): staff only. `past`: the rental has ended; `allowed`: this caller (a SuperAdmin) may cancel it. */
  past_cancel?: { past: boolean; allowed: boolean };
  /** 421: the rental's late extension owed by CARii's QR, with the receipt to decide on. */
  late_payment?: LatePaymentBlock | null;
  /** 374: the customer's photo windows for this rental; absent while customer photos are off. */
  photo_windows?: PhotoWindows;
  /** 322 (#589): manage-Booking only; set while a received payment that did not match the amount due
      waits for staff, null once resolved. `received_minor` is null when the amount was unreadable. */
  payment_mismatch?: {
    payment_id: string;
    due_minor: number;
    received_minor: number | null;
    reason: 'amount' | 'retired';
    found_at: string;
  } | null;
}

interface BookingEvent {
  /** 053: events emit `id` only — there was never an `_id` consumer. */
  id: string;
  event_type: string;
  from_status?: string;
  to_status?: string;
  actor_type: string;
  notes?: string;
  /** Staff only. 410: `past_cancel` marks a SuperAdmin's past cancellation. */
  metadata?: Record<string, unknown>;
  created_at: string;
}

interface EventsResponse {
  items: BookingEvent[];
}

// 300 (#403): 'cancelled' used to be listed per-status here (only from `pending`/`confirmed`),
// which meant clicking it went through the SAME code path as every other transition — no reason
// prompt. It is now its own always-available control (below, gated on non-terminal status
// instead of this table) so every cancellation, from any status, requires a reason.
const STATUS_TRANSITIONS: Record<string, string[]> = {
  pending: ['confirmed'],
  confirmed: ['checked_out'],
  // 407 (#1370): a checked-out or checked-in rental is finished with one End booking (below), not two steps.
};

/** 407: the statuses staff end a rental from (`POST /bookings/:id/end`). */
const ENDABLE_STATUSES = new Set(['checked_out', 'checked_in']);

const TERMINAL_STATUSES = new Set(['completed', 'cancelled', 'expired']);

/** 410 (#1472): the sentence the platform refuses a past cancel with, shown to anyone who may not. */
const PAST_CANCEL_REFUSED =
  'This rental has ended. Only staff allowed to change past bookings can cancel a booking whose rental has ended.';

// 300 (#403): a payment this box will offer to log a refund against. `status` decides visibility
// (below) — only a payment that has actually taken money has anything to refund.
interface PaymentSummary {
  id: string;
  status: string;
  amount_minor: number;
}

interface RefundEntry {
  id: string;
  amount_minor: number;
  currency: string;
  reason?: string;
  status: string;
  created_at: string;
}

// Refunds happen OUTSIDE this platform (bank transfer, etc.) — never through Bayarcash or any
// gateway from here. A payment with money still owed to refund sits in one of these two states.
const REFUNDABLE_PAYMENT_STATUSES = new Set(['paid', 'partially_refunded']);

/** 325: a leg's pickup and return, always in Malaysia time (as the bookings list shows them, 323). */
function formatRentalTime(iso: string) {
  return new Date(iso).toLocaleString('en-MY', {
    day: '2-digit',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  });
}

function formatMyr(minor: number) {
  return `RM ${(minor / 100).toFixed(2)}`;
}

/** 387: a release time as the mock-up shows it, in Malaysia time ("Oct 13, 2026, 11:42 PM"). */
function formatReleaseTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  });
}

export const LOCKBOX_REASON_MAX = 300;

function formatDate(iso: string) {
  return new Date(iso).toLocaleString('en-MY');
}

/**
 * 189 — the six refusals a reschedule can produce, worded for an operator.
 *
 * 🔴 Each has to READ differently, not merely exist. The backend chooses a specific code precisely so
 * the operator is told which of six quite different problems they have; collapsing them into one
 * "could not move booking" throws that away at the last step. `BookingDetailPage.reschedule.test.tsx`
 * asserts the six texts are distinct rather than snapshotting them.
 */
const RESCHEDULE_REFUSALS: Record<string, string> = {
  INVALID_WINDOW: 'The return time has to be after the pickup time.',
  BOOKING_NOT_RESCHEDULABLE: 'This rental is over, so its dates can no longer be changed.',
  BOOKING_HAS_EXTENSION:
    'This rental has been extended. Deal with the extension before moving its window.',
  OUTSIDE_OPERATING_HOURS:
    'That window falls outside the operating hours for this car and pickup location.',
  CAR_BLOCKED:
    'The car is unavailable then — there is maintenance or a staff block on that window.',
  SLOT_TAKEN: 'The car is already committed for that window by another booking.',
  // 328 (#736): an extended rental's times move through the move endpoint.
  BOOKING_TIME_LOCKED:
    'The car is out with the customer, so this rental’s times can no longer move; only its car can change.',
  NOTHING_TO_MOVE: 'That is where the rental already is, so there is nothing to move.',
};

/** 328: rental legs that no longer move with it (their part is over). */
const RENTAL_LEG_DONE = new Set(['cancelled', 'expired', 'completed']);

/** The span of a rental's live legs, or null when the booking was never extended (325's summary). */
function rentalSpanOf(
  booking: Booking | undefined,
): { start: number; end: number; legs: number } | null {
  const legs = booking?.rental?.legs ?? [];
  if (legs.length < 2) return null;
  const live = legs.filter((l) => !RENTAL_LEG_DONE.has(l.status));
  if (live.length === 0) return null;
  return {
    start: Math.min(...live.map((l) => Date.parse(l.pickup_at))),
    end: Math.max(...live.map((l) => Date.parse(l.return_at))),
    legs: legs.length,
  };
}

/**
 * 189 (D2) — what a proposed window would cost, as the preview endpoint publishes it.
 */
interface ReschedulePreview {
  current: {
    total_minor: number;
    amount_due_minor: number;
    override_total_minor: number | null;
    /** 190: today's price for the booking's OWN window. */
    requoted_total_minor: number;
  };
  proposed: { total_minor: number; currency: string; lines: unknown[] };
  difference_minor: number;
  /** 190: the part of the difference that predates the operator's edit. */
  repricing_drift_minor: number;
  /** 190: the part the edit actually causes — the number being decided about. */
  move_difference_minor: number;
  price_change_allowed: boolean;
  price_change_blocked_reason: 'MONEY_SETTLED' | 'NOT_PERMITTED' | null;
}

/**
 * 189 (D1, FR-022) — why the price cannot be changed, said out loud.
 *
 * 🔴 The two must read differently. A missing permission is something the operator can go and get; a
 * settled payment is not, and telling them "you do not have permission" when the real answer is
 * "this booking is paid for" sends them to ask for an ability that would still not help.
 */
const PRICE_BLOCKED: Record<string, string> = {
  MONEY_SETTLED:
    'This booking has already been paid, so its price cannot be changed here — lowering it would owe the customer a refund and raising it would owe a collection.',
  NOT_PERMITTED: 'You do not have permission to change a booking price.',
};

/**
 * The statuses whose window may still move — mirrors `reschedulePolicy.ts`'s own set.
 *
 * Exported (216, #312): the schedule's drag-to-reassign feature gates which booking bars offer the
 * drag on this same list, rather than restating it.
 */
export const RESCHEDULABLE_STATUSES = [
  'awaiting_payment',
  'confirmed',
  'checked_out',
  'checked_in',
];

const MYT_OFFSET_MS = 8 * 60 * 60 * 1000;

/**
 * 287 (#341): a UTC ISO instant as Malaysia wall-clock time, in `datetime-local`'s own
 * `YYYY-MM-DDTHH:mm` shape.
 *
 * Fixed +8h shift, mirroring `backend/src/modules/pricing/tz.ts`'s `mytParts` and `app/`'s own
 * `useDateTimePicker.ts` — deliberately NOT `Intl`/`toLocaleString`/the runtime's own `TZ`, so the
 * conversion is identical regardless of which machine or CI container runs it (FR-004).
 */
function mytDatetimeLocalValue(iso: string): string {
  const shifted = new Date(new Date(iso).getTime() + MYT_OFFSET_MS);
  const yyyy = shifted.getUTCFullYear();
  const mm = String(shifted.getUTCMonth() + 1).padStart(2, '0');
  const dd = String(shifted.getUTCDate()).padStart(2, '0');
  const hh = String(shifted.getUTCHours()).padStart(2, '0');
  const min = String(shifted.getUTCMinutes()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}T${hh}:${min}`;
}

/**
 * 287 (#341): the exact inverse of `mytDatetimeLocalValue` — a `datetime-local` value understood
 * as Malaysia wall-clock time, converted to the UTC instant it names.
 *
 * The ONE conversion used by both the reschedule-preview query and the Move booking submit (R5):
 * two independent implementations of this parse is exactly the drift risk that let those two paths
 * silently disagree before this fix.
 */
function mytDatetimeLocalToUtcIso(value: string): string {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) {
    throw new Error(`Not a datetime-local value: ${value}`);
  }
  const yyyy = Number(match[1]);
  const mm = Number(match[2]);
  const dd = Number(match[3]);
  const hh = Number(match[4]);
  const min = Number(match[5]);
  return new Date(Date.UTC(yyyy, mm - 1, dd, hh, min) - MYT_OFFSET_MS).toISOString();
}

/**
 * An ISO instant as a `datetime-local` input value, in Malaysia time.
 *
 * Deliberately string arithmetic rather than a locale format: the value has to round-trip through the
 * input and back into an ISO instant, and `toLocaleString` output does not parse back reliably.
 */
function toLocalInput(iso: string): string {
  return mytDatetimeLocalValue(iso);
}

/** 328: the rental's end for a new start (the length kept), or its current end while the start is unreadable. */
function followingEnd(pickupValue: string, span: { start: number; end: number }): string {
  try {
    const start = Date.parse(mytDatetimeLocalToUtcIso(pickupValue));
    return toLocalInput(new Date(start + (span.end - span.start)).toISOString());
  } catch {
    return toLocalInput(new Date(span.end).toISOString());
  }
}

export function BookingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const ability = useAbility();
  // 374: the page's cards, read by the jump links.
  const pageRef = useRef<HTMLDivElement>(null);
  const [selectedStatus, setSelectedStatus] = useState('');
  // 300 (#403): staff can cancel a booking of any non-terminal status, and must state why.
  const [cancelReason, setCancelReason] = useState('');
  // 410 (#1472): a SuperAdmin's past cancel: its reason and its confirm step.
  const [pastCancelReason, setPastCancelReason] = useState('');
  const [confirmPastCancel, setConfirmPastCancel] = useState(false);
  // 311 (#549): the confirm step in front of "Vehicle returned — release hold".
  const [confirmReturnOpen, setConfirmReturnOpen] = useState(false);
  // 322 (#589): the confirm step in front of "Accept and confirm" on a payment mismatch.
  const [confirmAcceptOpen, setConfirmAcceptOpen] = useState(false);
  const [overrideAmount, setOverrideAmount] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  // 189: the proposed window. Prefilled from the booking once it loads (below), because an operator
  // moving a return by two hours must not have to retype the pickup — and a blank field submitted
  // as-is is how a window loses an end.
  const [newPickup, setNewPickup] = useState('');
  const [newReturn, setNewReturn] = useState('');
  const [rescheduleError, setRescheduleError] = useState<string | null>(null);
  // 213 (#309): populated only when the refusal that set `rescheduleError` carried alternatives.
  const [rescheduleAlternatives, setRescheduleAlternatives] = useState<AlternativeCar[]>([]);

  const { data: booking, isLoading } = useQuery<Booking>({
    queryKey: RESOURCE_KEYS.booking.one(id as string),
    queryFn: () => api.get(`/bookings/${id}`),
    enabled: Boolean(id),
  });

  // The window the preview asks about: whatever the operator has typed, else the booking's own.
  // Declared here rather than after the loading guard because the preview query below needs it.
  // 328 (#736, US3): on an extended rental the card moves the WHOLE rental — it starts from the
  // rental's span, and the end follows the start so the length is kept (a length change is not a move).
  const rentalSpan = rentalSpanOf(booking);
  const previewPickup =
    newPickup ||
    (booking
      ? toLocalInput(rentalSpan ? new Date(rentalSpan.start).toISOString() : booking.pickup_at)
      : '');
  const previewReturn = rentalSpan
    ? followingEnd(previewPickup, rentalSpan)
    : newReturn || (booking ? toLocalInput(booking.return_at) : '');

  /**
   * 189 (D2): the preview. Keyed on the PROPOSED window, so editing either instant re-quotes — and
   * `enabled` waits for the booking, because the window is prefilled from it.
   *
   * 🔴 A failed preview is not an error state for this screen. An unpriceable window (a deleted
   * template, a duration the template cannot price) means no honest number exists, and the panel then
   * shows nothing rather than RM0.00 — 049's defect was a silent zero total becoming a free booking,
   * and a zero rendered here is something an operator could accept.
   */
  const { data: preview } = useQuery<ReschedulePreview>({
    queryKey: ['reschedule-preview', id, newPickup, newReturn],
    queryFn: () =>
      api.get(
        `/bookings/${id}/reschedule-preview?pickup_at=${encodeURIComponent(
          mytDatetimeLocalToUtcIso(previewPickup),
        )}&return_at=${encodeURIComponent(mytDatetimeLocalToUtcIso(previewReturn))}`,
      ),
    // 328: a rental keeps every price on a time move, so the single-booking preview is not asked.
    enabled: Boolean(id) && Boolean(previewPickup) && Boolean(previewReturn) && !rentalSpan,
    retry: false,
  });

  const { data: eventsData } = useQuery<EventsResponse>({
    queryKey: ['booking-events', id],
    queryFn: () => api.get(`/bookings/${id}/events`),
    enabled: Boolean(id),
  });

  // 407 (#1370): staff end a rental at any time, without return photos; recorded as ended by staff.
  const [confirmEnd, setConfirmEnd] = useState(false);
  const endRental = useMutation({
    mutationFn: () => api.post(`/bookings/${id}/end`),
    onSuccess: () => {
      setConfirmEnd(false);
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.one(id as string) });
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.list });
      void queryClient.invalidateQueries({ queryKey: ['booking-events', id] });
      void queryClient.invalidateQueries({ queryKey: ['booking-late-status', id] });
    },
  });

  const updateStatus = useMutation({
    mutationFn: (vars: { status: string; reason?: string }) =>
      api.patch(`/bookings/${id}/status`, { status: vars.status, reason: vars.reason }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.one(id as string) });
      // 161: found by T026's sweep — the bookings list shows status, and this is the screen that
      // changes it, so the list has to be told.
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.list });
      void queryClient.invalidateQueries({ queryKey: ['booking-events', id] });
      setSelectedStatus('');
      setCancelReason('');
    },
  });

  // 410 (#1472): the same status route; the platform cancels the whole rental, silently.
  const pastCancel = useMutation({
    mutationFn: (reason: string) =>
      api.patch(`/bookings/${id}/status`, { status: 'cancelled', reason }),
    onSuccess: () => {
      setConfirmPastCancel(false);
      setPastCancelReason('');
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.one(id as string) });
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.list });
      void queryClient.invalidateQueries({ queryKey: ['booking-events', id] });
    },
  });

  // 311 (#549, US2): release the hold a checked_out cancellation kept, once the car is back.
  const markReturned = useMutation({
    mutationFn: () => api.post(`/bookings/${id}/vehicle-returned`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.one(id as string) });
      void queryClient.invalidateQueries({ queryKey: ['booking-events', id] });
    },
    // Close the confirm step either way, so a refusal is read on the card, not in a stale dialog.
    onSettled: () => setConfirmReturnOpen(false),
  });

  // 387 (#1308, FR-007): release the lockbox code without pickup photos.
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [releaseReason, setReleaseReason] = useState('');
  const releaseLockbox = useMutation({
    mutationFn: () =>
      api.post(`/bookings/${id}/lockbox-release`, {
        ...(releaseReason.trim() ? { reason: releaseReason.trim() } : {}),
      }),
    onSuccess: () => {
      setReleaseReason('');
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.one(id as string) });
      void queryClient.invalidateQueries({ queryKey: ['booking-events', id] });
    },
    onSettled: () => setReleaseOpen(false),
  });

  // 322 (#589, FR-012): accept a received payment that did not match, confirming the booking.
  const acceptMismatch = useMutation({
    mutationFn: () => api.post(`/bookings/${id}/payment-mismatch/accept`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.one(id as string) });
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.list });
      void queryClient.invalidateQueries({ queryKey: ['booking-events', id] });
    },
    // Close the confirm step either way, so a refusal is read on the card, not in a stale dialog.
    onSettled: () => setConfirmAcceptOpen(false),
  });

  const setOverride = useMutation({
    mutationFn: () =>
      api.patch(`/bookings/${id}/price-override`, {
        override_total_minor: Math.round(Number.parseFloat(overrideAmount) * 100),
        reason: overrideReason,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.one(id as string) });
      setOverrideAmount('');
      setOverrideReason('');
    },
  });

  /**
   * 189 (#34) — move the booking's window.
   *
   * 🔴 Goes through `useSaveMutation` with `affects: ['schedule']`, and the second half is the part
   * that matters. A reschedule moves a car's OCCUPANCY, so the fleet schedule the operator just came
   * from is stale too. Invalidating only the booking leaves them pressing Back onto the pre-move day
   * — 161's defect exactly, and hidden by a full browser reload, which is why the reporter's
   * workaround was refreshing.
   */
  const reschedule = useSaveMutation<{ pickup_at: string; return_at: string }>({
    resource: 'booking',
    id: id as string,
    affects: ['schedule'],
    mutationFn: (body) => api.patch(`/bookings/${id}/schedule`, body),
    onSaved: () => {
      setRescheduleError(null);
      setRescheduleAlternatives([]);
      void queryClient.invalidateQueries({ queryKey: ['booking-events', id] });
    },
    onError: (err) => {
      const code = (err as { code?: string }).code ?? '';
      // The mapped sentence when the code is one we know; the server's own message otherwise, which
      // is still better than a generic failure. `||` not `??`: an empty message is falsy and must
      // fall through, which is the silent-failure shape 188 fixed on the customer's own screen.
      setRescheduleError(
        RESCHEDULE_REFUSALS[code] || (err as Error).message || 'The booking could not be moved.',
      );
      setRescheduleAlternatives((err as { alternatives?: AlternativeCar[] }).alternatives ?? []);
    },
  });

  /** 328 (#736, US3): shift the whole rental by the new start, on the same car. */
  const moveRental = useSaveMutation<{ car_id: string; shift_ms: number }>({
    resource: 'booking',
    id: id as string,
    affects: ['schedule'],
    mutationFn: (body) => api.patch(`/bookings/${id}/move`, body),
    onSaved: () => {
      setRescheduleError(null);
      setRescheduleAlternatives([]);
      setNewPickup('');
      void queryClient.invalidateQueries({ queryKey: ['booking-events', id] });
    },
    onError: (err) => {
      const code = (err as { code?: string }).code ?? '';
      setRescheduleError(
        RESCHEDULE_REFUSALS[code] || (err as Error).message || 'The rental could not be moved.',
      );
      setRescheduleAlternatives((err as { alternatives?: AlternativeCar[] }).alternatives ?? []);
    },
  });

  const clearOverride = useMutation({
    mutationFn: () => api.patch(`/bookings/${id}/price-override`, { clear: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: RESOURCE_KEYS.booking.one(id as string) });
    },
  });

  /**
   * 203 (#286) — reassign the booking to a different car. Separate control from Reschedule
   * (research.md/spec decision) — the window never changes here, only the car and its price.
   */
  const [carPlateInput, setCarPlateInput] = useState('');
  const [carPlateQuery, setCarPlateQuery] = useState('');
  const [changeCarId, setChangeCarId] = useState('');
  const [changeCarError, setChangeCarError] = useState<string | null>(null);
  // 213 (#309): populated only when the refusal that set `changeCarError` carried alternatives.
  const [changeCarAlternatives, setChangeCarAlternatives] = useState<AlternativeCar[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setCarPlateQuery(carPlateInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [carPlateInput]);

  const { data: carOptions } = useQuery({
    queryKey: ['admin-cars-search', carPlateQuery],
    queryFn: () =>
      api.get<{ items: Array<{ _id: string; name: string; plate_number: string }> }>(
        `/cars?limit=10&plate_number=${encodeURIComponent(carPlateQuery)}`,
      ),
    enabled: Boolean(carPlateQuery),
  });

  const { data: changeCarPreview } = useQuery({
    queryKey: ['change-car-preview', id, changeCarId, booking?.pickup_at, booking?.return_at],
    queryFn: () =>
      api.post<{
        outcome: string;
        pricing_template_id?: string;
        currency_symbol?: string;
        grand_total_minor?: number;
        total_minor?: number;
      }>('/pricing/quote', {
        car_id: changeCarId,
        pickup_at: booking?.pickup_at,
        return_at: booking?.return_at,
      }),
    enabled: Boolean(changeCarId) && Boolean(booking),
  });

  const changeCar = useSaveMutation<{ car_id: string; pricing_template_id: string }>({
    resource: 'booking',
    id: id as string,
    affects: ['schedule'],
    mutationFn: (body) => api.patch(`/bookings/${id}/car`, body),
    onSaved: () => {
      setChangeCarError(null);
      setChangeCarAlternatives([]);
      setChangeCarId('');
      setCarPlateInput('');
      void queryClient.invalidateQueries({ queryKey: ['booking-events', id] });
    },
    onError: (err) => {
      setChangeCarError((err as Error).message || 'The car could not be changed.');
      setChangeCarAlternatives((err as { alternatives?: AlternativeCar[] }).alternatives ?? []);
    },
  });

  /**
   * 288 — a SEPARATE action from Change Car above, deliberately: this one works even on a
   * money-settled booking (the whole reason it exists — a car that breaks down after payment),
   * price is always frozen (no preview, no pricing_template_id — spec Clarifications), and it is
   * gated on `reassignCarOnBreakdown`, not `manage`, so a Supplier/Host sees it for their own
   * fleet's bookings too (research.md D2).
   */
  const [breakdownPlateInput, setBreakdownPlateInput] = useState('');
  const [breakdownPlateQuery, setBreakdownPlateQuery] = useState('');
  const [breakdownCarId, setBreakdownCarId] = useState('');
  const [breakdownError, setBreakdownError] = useState<string | null>(null);
  const [breakdownAlternatives, setBreakdownAlternatives] = useState<AlternativeCar[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setBreakdownPlateQuery(breakdownPlateInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [breakdownPlateInput]);

  const { data: breakdownCarOptions } = useQuery({
    queryKey: ['admin-cars-search-breakdown', breakdownPlateQuery],
    queryFn: () =>
      api.get<{ items: Array<{ _id: string; name: string; plate_number: string }> }>(
        `/cars?limit=10&plate_number=${encodeURIComponent(breakdownPlateQuery)}`,
      ),
    enabled: Boolean(breakdownPlateQuery),
  });

  const reassignCarOnBreakdown = useSaveMutation<{ car_id: string; reason: 'car_breakdown' }>({
    resource: 'booking',
    id: id as string,
    affects: ['schedule'],
    mutationFn: (body) => api.patch(`/bookings/${id}/car/breakdown`, body),
    onSaved: () => {
      setBreakdownError(null);
      setBreakdownAlternatives([]);
      setBreakdownCarId('');
      setBreakdownPlateInput('');
      void queryClient.invalidateQueries({ queryKey: ['booking-events', id] });
    },
    onError: (err) => {
      setBreakdownError((err as Error).message || 'The car could not be reassigned.');
      setBreakdownAlternatives((err as { alternatives?: AlternativeCar[] }).alternatives ?? []);
    },
  });

  /**
   * 300 (#400) — staff correction of a customer's booking mistake (wrong car and/or wrong
   * location). Unlike Change Car above, this reaches a PAID booking (price stays frozen — no
   * preview, no pricing_template_id) and unlike Car Broke Down above, the replacement car may be
   * at a DIFFERENT location. Admin-only (`manage`, not `reassignCarOnBreakdown` — research.md
   * Decision 3), and a reason is required so every correction is attributed.
   */
  const [correctionPlateInput, setCorrectionPlateInput] = useState('');
  const [correctionPlateQuery, setCorrectionPlateQuery] = useState('');
  const [correctionCarId, setCorrectionCarId] = useState('');
  const [correctionReason, setCorrectionReason] = useState('');
  const [correctionError, setCorrectionError] = useState<string | null>(null);
  const [correctionAlternatives, setCorrectionAlternatives] = useState<AlternativeCar[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setCorrectionPlateQuery(correctionPlateInput.trim()), 400);
    return () => clearTimeout(timer);
  }, [correctionPlateInput]);

  const { data: correctionCarOptions } = useQuery({
    queryKey: ['admin-cars-search-correction', correctionPlateQuery],
    queryFn: () =>
      api.get<{ items: Array<{ _id: string; name: string; plate_number: string }> }>(
        `/cars?limit=10&plate_number=${encodeURIComponent(correctionPlateQuery)}`,
      ),
    enabled: Boolean(correctionPlateQuery),
  });

  const correctCarAssignment = useSaveMutation<{ car_id: string; reason: string }>({
    resource: 'booking',
    id: id as string,
    affects: ['schedule'],
    mutationFn: (body) => api.patch(`/bookings/${id}/car/correction`, body),
    onSaved: () => {
      setCorrectionError(null);
      setCorrectionAlternatives([]);
      setCorrectionCarId('');
      setCorrectionPlateInput('');
      setCorrectionReason('');
      void queryClient.invalidateQueries({ queryKey: ['booking-events', id] });
    },
    onError: (err) => {
      setCorrectionError((err as Error).message || 'The car could not be changed.');
      setCorrectionAlternatives((err as { alternatives?: AlternativeCar[] }).alternatives ?? []);
    },
  });

  /**
   * 300 (#403) — logging a refund that happened OUTSIDE this platform. Posts to the
   * already-existing `POST /payments/:id/refund` (ledger-only, never calls Bayarcash or any
   * gateway) and reads back via the new `GET /payments/:id/refunds`. This repo's UI convention:
   * surface the server's own stated refusal reason rather than re-deriving the refundable limit
   * client-side (e.g. an amount exceeding what remains).
   */
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundError, setRefundError] = useState<string | null>(null);

  const { data: payment } = useQuery<PaymentSummary>({
    queryKey: ['payment', booking?.payment_id],
    queryFn: () => api.get(`/payments/${booking?.payment_id}`),
    enabled: Boolean(booking?.payment_id),
  });

  // 341 (#934): only a caller who may see refunds asks for them.
  const { data: refunds } = useQuery<RefundEntry[]>({
    queryKey: ['payment-refunds', booking?.payment_id],
    queryFn: () => api.get(`/payments/${booking?.payment_id}/refunds`),
    enabled: Boolean(booking?.payment_id) && ability.can('read', 'Refund'),
  });

  const logRefund = useMutation({
    mutationFn: () =>
      api.post(`/payments/${booking?.payment_id}/refund`, {
        amount_minor: Math.round(Number.parseFloat(refundAmount) * 100),
        reason: refundReason.trim(),
      }),
    onSuccess: () => {
      setRefundError(null);
      setRefundAmount('');
      setRefundReason('');
      void queryClient.invalidateQueries({ queryKey: ['payment', booking?.payment_id] });
      void queryClient.invalidateQueries({ queryKey: ['payment-refunds', booking?.payment_id] });
    },
    onError: (err) => {
      setRefundError((err as Error).message || 'The refund could not be logged.');
    },
  });

  if (isLoading || !booking) {
    return <div className="text-muted text-sm">Loading…</div>;
  }

  const transitions = STATUS_TRANSITIONS[booking.status] ?? [];
  // 410 (#1472): a past rental is cancelled only through the past card (SuperAdmin), never the ordinary control.
  const isPast = booking.past_cancel?.past === true;
  const pastLegs = (booking.rental?.legs ?? []).filter(
    (l) => l.status !== 'cancelled' && l.status !== 'expired',
  );
  const pastLegCount = Math.max(pastLegs.length, 1);
  const pastCredit = booking.pricing_snapshot?.totals?.gift_cert_credit_minor ?? 0;

  // 189: offered only where it can actually be used — the ability AND a rental that is not over.
  // FR-031: a control that will only ever refuse is worse than no control.
  const canReschedule =
    ability.can('reschedule', 'Booking') && RESCHEDULABLE_STATUSES.includes(booking.status);
  // 203 (#286): deliberately `manage`, not `reschedule` — Supplier/Host hold `reschedule` for their
  // own fleet, but the replacement car may belong to any supplier (research.md R7).
  const canChangeCar =
    ability.can('manage', 'Booking') && RESCHEDULABLE_STATUSES.includes(booking.status);
  // 288: Admin (any booking, via manage) or the owning Supplier/Host (own fleet) — deliberately
  // NOT gated on payment status, since this action's whole purpose is a booking that IS paid.
  const canReassignBreakdown =
    ability.can('reassignCarOnBreakdown', 'Booking') &&
    RESCHEDULABLE_STATUSES.includes(booking.status);
  // 300 (#400): Admin-only, same gate as Change Car — but reaches a PAID booking (price frozen)
  // and allows a DIFFERENT location, which neither Change Car nor Car Broke Down permit.
  const canCorrectCar =
    ability.can('manage', 'Booking') && RESCHEDULABLE_STATUSES.includes(booking.status);
  // 300 (#403): Admin-only, and only once the payment has actually taken money — a payment that
  // was never paid, or a booking with no payment_id at all, has nothing to refund.
  // 341 (#934): the refund permissions, not `manage Payment`: `read Refund` shows the box and its list, `create Refund`
  // adds the form. The payment itself must be readable (`read Payment`) to know there is money to refund.
  const refundablePayment =
    Boolean(payment) && REFUNDABLE_PAYMENT_STATUSES.has(payment?.status ?? '');
  const canLogRefund = ability.can('create', 'Refund') && refundablePayment;
  const canSeeRefunds =
    (ability.can('read', 'Refund') || ability.can('create', 'Refund')) && refundablePayment;
  const pickupValue = previewPickup;
  const returnValue = previewReturn;

  /**
   * 189 (D1): the SERVER decides whether the price may change, and the screen asks rather than
   * guessing.
   *
   * 🔴 This replaces `booking.status !== 'awaiting_payment'`, which was wrong in a way that got worse
   * after 188: an unpaid booking now holds no slot and so has no schedule bar, meaning the one status
   * the old condition allowed is almost never the status an operator is looking at. `undefined` (no
   * preview yet, or an unpriceable window) is treated as ALLOWED, so the controls do not flicker
   * disabled while the preview loads — the endpoint refuses regardless, so nothing rests on this.
   */
  /**
   * 🔴 Shape-checked, not merely truthy-checked. Found by an existing suite whose api stub answers
   * `{items: []}` for any unrecognised path — that value is truthy, so a `preview && …` guard let
   * `preview.current.total_minor` throw and took the WHOLE PAGE down, not just the panel. The same
   * shape reaches production from a backend that predates this route or from any payload that is not
   * this contract, and a booking screen that white-screens is far worse than one missing a preview.
   */
  const quoted =
    preview &&
    typeof preview.current?.total_minor === 'number' &&
    typeof preview.move_difference_minor === 'number' &&
    preview.proposed
      ? preview
      : null;
  const priceBlockedReason =
    quoted && !quoted.price_change_allowed
      ? (quoted.price_change_blocked_reason ?? 'NOT_PERMITTED')
      : null;
  const priceLocked = priceBlockedReason !== null;

  return (
    <div className="max-w-3xl">
      <PageHeader
        title={`Booking ${booking.id.slice(-8)}`}
        fallback="/bookings"
        actions={
          booking.receipt_available === true ? (
            <ReceiptButton
              bookingId={booking.id}
              rentalId={booking.rental?.rental_id ?? booking.id}
            />
          ) : undefined
        }
      />

      <div className="grid gap-4" ref={pageRef}>
        {/* 374 (US6): the booking at a glance, and links to each part of this long page. */}
        <BookingSummaryLine
          status={booking.status}
          car={booking.car}
          pickupAt={booking.rental?.pickup_at ?? booking.pickup_at}
          returnAt={booking.rental?.return_at ?? booking.return_at}
          customer={booking.customer}
          photoWindows={booking.photo_windows}
        />
        {/* 377 (#1233, design F): who handles this car. */}
        {booking.car_id && <CarHostsLine carId={booking.car_id} />}
        {/* 408 (#1434, mock-up screen 5): the car's fuel now, from Cartrack. */}
        {booking.car?.fuel && (
          <Card data-testid="booking-fuel">
            <dl className="grid grid-cols-[130px_1fr] gap-x-4 text-sm">
              <dt className="text-steel">Fuel now</dt>
              <dd>
                <FuelBars fuel={booking.car.fuel} />
              </dd>
            </dl>
          </Card>
        )}
        <BookingJumpLinks containerRef={pageRef} />
        {/* 322 (#589, FR-011): a payment arrived that did not match the amount due (or on a link a
            price change replaced), so the booking was NOT confirmed. The customer has paid, so the
            car stays held until staff accept it or cancel the booking. The notice shows to anyone
            who receives the field; the action only to `manage Booking`, which the backend enforces. */}
        {booking.payment_mismatch && (
          <Card data-testid="payment-mismatch-card">
            <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
              Payment does not match
            </h2>
            <p className="text-sm text-ink">
              {booking.payment_mismatch.reason === 'retired'
                ? 'The customer paid on a link that was replaced after a price change. '
                : 'The customer paid an amount different from the amount due. '}
              The booking is not confirmed, and the car stays held for it until this is resolved.
            </p>
            <dl className="mt-3 grid grid-cols-2 gap-x-8 gap-y-1 text-sm">
              <dt className="text-steel">Amount due</dt>
              <dd>{formatMyr(booking.payment_mismatch.due_minor)}</dd>
              <dt className="text-steel">Amount received</dt>
              <dd>
                {booking.payment_mismatch.received_minor == null
                  ? 'The amount could not be read'
                  : formatMyr(booking.payment_mismatch.received_minor)}
              </dd>
            </dl>
            <p className="mt-3 text-sm text-steel">
              Accept it to confirm the booking and tell the customer, or cancel the booking.
            </p>
            {/* 341 (#934): or a role holding only `accept-mismatch Payment`. */}
            {(ability.can('manage', 'Booking') || ability.can('accept-mismatch', 'Payment')) && (
              <div className="mt-3">
                <Button size="sm" variant="primary" onClick={() => setConfirmAcceptOpen(true)}>
                  Accept and confirm
                </Button>
              </div>
            )}
            {acceptMismatch.isError && (
              <p className="mt-2 text-sm text-red-500">
                {(acceptMismatch.error as { message?: string }).message ??
                  'Failed to accept the payment'}
              </p>
            )}
          </Card>
        )}
        <Modal
          open={confirmAcceptOpen && Boolean(booking.payment_mismatch)}
          onClose={() => setConfirmAcceptOpen(false)}
          title="Confirm this booking?"
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setConfirmAcceptOpen(false)}>
                Go back
              </Button>
              <Button
                size="sm"
                variant="primary"
                loading={acceptMismatch.isPending}
                onClick={() => acceptMismatch.mutate()}
              >
                Confirm booking
              </Button>
            </div>
          }
        >
          <p className="text-sm">
            {booking.payment_mismatch &&
              `You accept ${
                booking.payment_mismatch.received_minor == null
                  ? 'a payment whose amount could not be read'
                  : formatMyr(booking.payment_mismatch.received_minor)
              } against ${formatMyr(booking.payment_mismatch.due_minor)} due. `}
            The booking is confirmed and the customer is told. Any difference is settled outside the
            platform.
          </p>
        </Modal>
        <Card id="details" data-jump="Details">
          <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">Details</h2>
          <dl className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <dt className="text-steel">Status</dt>
            <dd>
              <StatusBadge status={booking.status} />
            </dd>

            {/* #17: who rented, and the way through to their account. Rendered only when the
                payload carries the field, so a pre-#17 backend shows no empty row. */}
            {booking.customer !== undefined && (
              <>
                <dt className="text-steel">Customer</dt>
                <dd>
                  {booking.customer ? (
                    <>
                      <Link
                        to={`/users/${booking.customer.id}`}
                        className="text-primary hover:underline"
                      >
                        {booking.customer.name}
                      </Link>
                      <div className="text-steel">{booking.customer.email}</div>
                      {/* #24: staff phone the renter from here, so an account with no number
                          says so rather than leaving a blank line they might read as a bug. */}
                      <div className="text-steel">
                        {booking.customer.phone ?? 'No phone number recorded'}
                      </div>
                    </>
                  ) : (
                    <span className="text-steel">This account no longer exists</span>
                  )}
                </dd>
              </>
            )}

            <dt className="text-steel">Pickup</dt>
            <dd>{formatDate(booking.pickup_at)}</dd>

            <dt className="text-steel">Return</dt>
            <dd>{formatDate(booking.return_at)}</dd>

            {/* 361 (design 1g): the customer's trip answers. */}
            {booking.trip ? (
              <>
                <dt className="text-steel">Students in the car</dt>
                <dd data-testid="booking-trip-students">{booking.trip.students}</dd>
                <dt className="text-steel">Purpose</dt>
                <dd data-testid="booking-trip-purpose">{purposeLabel(booking.trip.purpose)}</dd>
                <dt className="text-steel">OK to do food delivery</dt>
                <dd data-testid="booking-trip-delivery">
                  {booking.trip.food_delivery ? 'Yes' : 'No'}
                </dd>
                <dt className="text-steel">OK with non-halal food</dt>
                <dd data-testid="booking-trip-non-halal">
                  {booking.trip.non_halal === undefined
                    ? 'Not asked'
                    : booking.trip.non_halal
                      ? 'Yes'
                      : 'No'}
                </dd>
              </>
            ) : (
              <>
                <dt className="text-steel">Trip</dt>
                <dd data-testid="booking-trip-none" className="text-steel">
                  Not answered
                </dd>
              </>
            )}

            <dt className="text-steel">Base price</dt>
            <dd>{formatMyr(booking.base_price_minor)}</dd>

            <dt className="text-steel">Discount</dt>
            <dd>{formatMyr(booking.discount_minor)}</dd>

            <dt className="text-steel">Total</dt>
            <dd className="font-semibold">{formatMyr(booking.total_price_minor)}</dd>

            {/* 358 (FR-026): the credit this booking used, certificate by certificate, and what is left to pay. */}
            {(booking.pricing_snapshot?.totals?.gift_cert_credit_minor ?? 0) > 0 && (
              <>
                <dt className="text-steel">Credit used</dt>
                <dd>
                  <span data-testid="credit-used" className="font-semibold">
                    {formatMyr(booking.pricing_snapshot?.totals?.gift_cert_credit_minor ?? 0)}
                  </span>
                  {booking.credit_draws && booking.credit_draws.length > 0 && (
                    <ul className="mt-1 space-y-0.5 text-xs text-steel">
                      {booking.credit_draws.map((d) => (
                        <li key={d.gift_certificate_id} data-testid="credit-draw">
                          {`${d.code ?? 'Deleted certificate'} · ${formatMyr(d.amount_minor)}`}
                        </li>
                      ))}
                    </ul>
                  )}
                </dd>
                <dt className="text-steel">Amount due</dt>
                <dd data-testid="credit-amount-due">
                  {formatMyr(booking.pricing_snapshot?.totals?.amount_due_minor ?? 0)}
                </dd>
              </>
            )}

            {booking.pricing_snapshot?.totals?.non_refundable_minor !== undefined && (
              <>
                <dt className="text-steel">Non-refundable</dt>
                <dd data-testid="non-refundable-amount">
                  {formatMyr(booking.pricing_snapshot.totals.non_refundable_minor)}
                </dd>
              </>
            )}

            {booking.notes && (
              <>
                <dt className="text-steel">Notes</dt>
                <dd>{booking.notes}</dd>
              </>
            )}
            {booking.cancellation_reason && (
              <>
                <dt className="text-steel">Cancellation</dt>
                <dd className="text-red-600">{booking.cancellation_reason}</dd>
              </>
            )}

            {/* 304: the only navigation between a chain's bookings now that /booking-extensions
                is gone — an extension is a linked child Booking, not a separate entity. */}
            {booking.parent_booking_id && (
              <>
                <dt className="text-steel">Extended from</dt>
                <dd>
                  <Link
                    to={`/bookings/${booking.parent_booking_id}`}
                    className="text-primary hover:underline"
                  >
                    Booking {booking.parent_booking_id.slice(-8)}
                  </Link>
                </dd>
              </>
            )}
            {booking.child_booking_id && (
              <>
                <dt className="text-steel">Extended into</dt>
                <dd>
                  <Link
                    to={`/bookings/${booking.child_booking_id}`}
                    className="text-primary hover:underline"
                  >
                    Booking {booking.child_booking_id.slice(-8)}
                  </Link>
                </dd>
              </>
            )}
          </dl>
        </Card>

        {/* 325 (#477): every leg of the rental, the original booking and each extension, with its
            own status and price and a link to open it. Shown only for a rental of more than one leg. */}
        {booking.rental && booking.rental.legs.length > 1 && (
          <Card>
            <div data-testid="rental-legs">
              <h2 className="text-sm font-semibold text-ink mb-3">Rental</h2>
              <ul className="flex flex-col gap-2">
                {booking.rental.legs.map((leg, i) => (
                  <li key={leg.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                    <span className="font-medium text-ink">
                      {leg.is_extension ? `Extension ${i}` : 'Original booking'}
                    </span>
                    <Link
                      to={`/bookings/${leg.id}`}
                      className="font-mono text-xs text-primary hover:underline"
                    >
                      {leg.id.slice(-8)}
                    </Link>
                    <span className="text-steel">
                      {formatRentalTime(leg.pickup_at)} → {formatRentalTime(leg.return_at)}
                    </span>
                    <StatusBadge status={leg.status} />
                    <span className="ml-auto text-ink">{formatMyr(leg.total_price_minor)}</span>
                  </li>
                ))}
              </ul>
              <div className="mt-3 flex justify-between border-t border-hairline pt-2 text-sm">
                <span className="text-steel">Rental total</span>
                <span data-testid="rental-legs-total" className="font-semibold text-ink">
                  {formatMyr(booking.rental.total_price_minor)}
                </span>
              </div>
            </div>
          </Card>
        )}

        {/* 305: extracted, with photographs and the recorder added. 374 (US5, US6): the Car condition card, moved up
            to follow Details and Rental, with the customer's photo windows. */}
        {id && (
          <InspectionsSection
            bookingId={id}
            // 311 (#549, US4, FR-011): recording the car's return on a cancelled booking that still
            // holds it is the natural moment to release the hold — offer it, never do it silently.
            photoWindows={booking.photo_windows}
            pickupAt={booking.rental?.pickup_at ?? booking.pickup_at}
            returnAt={booking.rental?.return_at ?? booking.return_at}
            onCheckinSaved={() => {
              if (booking.status === 'cancelled' && booking.holds_car) setConfirmReturnOpen(true);
            }}
          />
        )}

        {/* 325 (#477, US4): staff extend a customer's rental. Renders only on an active rental's
            last leg; admin-only, the same right as recording a booking for a customer. */}
        {ability.can('manage', 'Booking') && <ExtendRentalCard booking={booking} />}

        {/* 421 (#1528): a late extension the customer pays by CARii's QR, and its receipt. */}
        {booking.late_payment && (
          <LatePaymentCard latePayment={booking.late_payment} legs={booking.rental?.legs ?? []} />
        )}

        {/* 371 (#1241): the rental's penalties. 412: for staff who may read them platform-wide. */}
        {holdsAnywhere(ability as never, 'read', 'Penalty') && (
          <BookingPenaltiesCard booking={booking} />
        )}

        {/* 189 (#34) — move the window. Placed above the price card because the price question only
            arises after the window has moved, and the operator reads down the page. */}
        {canReschedule && (
          <Card data-testid="reschedule-card" data-jump="Change booking">
            <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
              Reschedule
            </h2>
            {rentalSpan && (
              <p data-testid="reschedule-rental-note" className="mb-3 text-sm text-steel">
                {`This booking is part of a rental of ${rentalSpan.legs} bookings. Moving it moves the whole rental: the end follows the start, and every price is kept.`}
              </p>
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              <label className="flex flex-col gap-1 text-sm" htmlFor="new-pickup">
                <span className="text-steel">New pickup</span>
                <Input
                  id="new-pickup"
                  type="datetime-local"
                  value={pickupValue}
                  onChange={(e) => setNewPickup((e.target as HTMLInputElement).value)}
                />
              </label>
              <label className="flex flex-col gap-1 text-sm" htmlFor="new-return">
                <span className="text-steel">New return</span>
                <Input
                  id="new-return"
                  type="datetime-local"
                  value={returnValue}
                  disabled={Boolean(rentalSpan)}
                  onChange={(e) => setNewReturn((e.target as HTMLInputElement).value)}
                />
              </label>
            </div>

            {/* 189 design review: `text-semantic-danger-text`, not Tailwind's stock `text-red-600`.
                The token exists BECAUSE it carries a light/dark pair (index.css:36 and :81) — its own
                comment says a saturated red has to shift between modes — and this panel is new text,
                so it starts on the token rather than inheriting the older literal used elsewhere.
                Constitution Principle V: the tokens are the source of truth. */}
            {rescheduleError && (
              <>
                <p
                  data-testid="reschedule-error"
                  className="mt-3 text-sm text-semantic-danger-text"
                >
                  {rescheduleError}
                </p>
                <AlternativeCars items={rescheduleAlternatives} />
              </>
            )}

            <div className="mt-3 flex items-center gap-2">
              <Button
                onClick={() => {
                  setRescheduleError(null);
                  if (rentalSpan) {
                    // 328: the whole rental moves by the new start's distance, on the same car.
                    void moveRental.save({
                      car_id: booking.car_id,
                      shift_ms:
                        Date.parse(mytDatetimeLocalToUtcIso(pickupValue)) - rentalSpan.start,
                    });
                    return;
                  }
                  // Both instants, always. A partial window is ambiguous about which end the
                  // operator meant to keep, and the endpoint refuses one on purpose.
                  void reschedule.save({
                    pickup_at: mytDatetimeLocalToUtcIso(pickupValue),
                    return_at: mytDatetimeLocalToUtcIso(returnValue),
                  });
                }}
                disabled={reschedule.isPending || moveRental.isPending}
              >
                {rentalSpan ? 'Move rental' : 'Move booking'}
              </Button>
              {/* D-f: #34 asked for this on the schedule page. It is one press away from a bar and
                  one link back, and the link is the second half of that claim. */}
              <Link to="/schedule" className="text-sm text-primary hover:underline">
                Back to schedule
              </Link>
            </div>
            <CustomerToldNote />
          </Card>
        )}

        {/* 203 (#286): a separate box from Reschedule — the window never changes here, only the
            car and its price. */}
        {canChangeCar && (
          <Card data-testid="change-car-card" data-jump="Change booking">
            <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
              Change Car
            </h2>
            {/* 300 (#400): this control re-prices the booking, so it refuses once payment has
                settled (MONEY_SETTLED) — use Correct Car / Location below for a paid booking. */}
            <p className="text-sm text-steel mb-2">
              For a not-yet-paid booking. Once paid, use Correct Car / Location below instead.
            </p>
            {booking.car && (
              <p className="text-sm text-steel mb-2">
                Current: {booking.car.label} — {booking.car.plate_number}
              </p>
            )}
            <label className="flex flex-col gap-1 text-sm" htmlFor="change-car-plate">
              <span className="text-steel">Replacement car</span>
              <Input
                id="change-car-plate"
                type="text"
                placeholder="Search by plate number"
                value={carPlateInput}
                onChange={(e) => {
                  setCarPlateInput((e.target as HTMLInputElement).value);
                  setChangeCarId('');
                }}
              />
            </label>
            {carOptions?.items && carOptions.items.length > 0 && !changeCarId && (
              <div className="grid gap-1 mt-2">
                {carOptions.items.map((c) => (
                  <button
                    key={c._id}
                    type="button"
                    className="text-left text-sm px-3 py-2 rounded border border-hairline hover:bg-surface-alt"
                    onClick={() => {
                      setChangeCarId(c._id);
                      setCarPlateInput(c.plate_number);
                    }}
                  >
                    {c.name} — {c.plate_number}
                  </button>
                ))}
              </div>
            )}
            {changeCarId && (
              <p className="text-sm text-steel mt-2" data-testid="change-car-preview">
                {changeCarPreview?.outcome === 'priced'
                  ? `Price on this car: ${changeCarPreview.currency_symbol ?? ''} ${(
                      (changeCarPreview.grand_total_minor ?? changeCarPreview.total_minor ?? 0) /
                        100
                    ).toFixed(2)}`
                  : 'Resolving price…'}
              </p>
            )}
            {/* 386 (#1278, FR-011): the gap on the chosen car — a warning only. */}
            {changeCarId && booking && (
              <div className="mt-2">
                <GapWarning
                  carId={changeCarId}
                  pickupAt={booking.pickup_at}
                  returnAt={booking.return_at}
                  bookingId={booking.id ?? id}
                />
              </div>
            )}
            {changeCarError && (
              <>
                <p className="mt-3 text-sm text-semantic-danger-text">{changeCarError}</p>
                <AlternativeCars
                  items={changeCarAlternatives}
                  onSelect={(car) => {
                    setChangeCarId(car.id);
                    setCarPlateInput(car.name);
                  }}
                />
              </>
            )}
            <div className="mt-3">
              <Button
                onClick={() => {
                  setChangeCarError(null);
                  if (!changeCarPreview?.pricing_template_id) {
                    setChangeCarError('No price could be resolved for this car.');
                    return;
                  }
                  void changeCar.save({
                    car_id: changeCarId,
                    pricing_template_id: changeCarPreview.pricing_template_id,
                  });
                }}
                disabled={!changeCarId || changeCar.isPending}
              >
                Confirm car change
              </Button>
            </div>
            <CustomerToldNote />
          </Card>
        )}

        {canReassignBreakdown && (
          <Card data-testid="breakdown-reassign-card" data-jump="Change booking">
            <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
              Car Broke Down
            </h2>
            <p className="text-sm text-steel mb-2">
              Move this booking onto a different car at the same location. The price stays exactly
              as it is — this does not change what the customer is charged.
            </p>
            {booking.car && (
              <p className="text-sm text-steel mb-2">
                Current: {booking.car.label} — {booking.car.plate_number}
              </p>
            )}
            <label className="flex flex-col gap-1 text-sm" htmlFor="breakdown-car-plate">
              <span className="text-steel">Replacement car (same location only)</span>
              <Input
                id="breakdown-car-plate"
                type="text"
                placeholder="Search by plate number"
                value={breakdownPlateInput}
                onChange={(e) => {
                  setBreakdownPlateInput((e.target as HTMLInputElement).value);
                  setBreakdownCarId('');
                }}
              />
            </label>
            {breakdownCarOptions?.items &&
              breakdownCarOptions.items.length > 0 &&
              !breakdownCarId && (
                <div className="grid gap-1 mt-2">
                  {breakdownCarOptions.items.map((c) => (
                    <button
                      key={c._id}
                      type="button"
                      className="text-left text-sm px-3 py-2 rounded border border-hairline hover:bg-surface-alt"
                      onClick={() => {
                        setBreakdownCarId(c._id);
                        setBreakdownPlateInput(c.plate_number);
                      }}
                    >
                      {c.name} — {c.plate_number}
                    </button>
                  ))}
                </div>
              )}
            {breakdownError && (
              <>
                <p className="mt-3 text-sm text-semantic-danger-text">{breakdownError}</p>
                <AlternativeCars
                  items={breakdownAlternatives}
                  onSelect={(car) => {
                    setBreakdownCarId(car.id);
                    setBreakdownPlateInput(car.name);
                  }}
                />
              </>
            )}
            <div className="mt-3">
              <Button
                onClick={() => {
                  setBreakdownError(null);
                  void reassignCarOnBreakdown.save({
                    car_id: breakdownCarId,
                    reason: 'car_breakdown',
                  });
                }}
                disabled={!breakdownCarId || reassignCarOnBreakdown.isPending}
              >
                Confirm reassignment
              </Button>
            </div>
            <CustomerToldNote />
            {/* spec Assumptions: a reminder, not a requirement — this feature deliberately does not
                take the old car out of service itself; the existing maintenance-block action does. */}
            <p className="mt-3 text-xs text-steel">
              Remember to also mark the old car under maintenance on the schedule, so it isn't
              booked again while it's out of service.
            </p>
          </Card>
        )}

        {/* 300 (#400): the staff correction tool — reaches a paid booking, price frozen, and the
            replacement car may be at a DIFFERENT location (unlike Change Car and Car Broke Down
            above, both of which require the same location). */}
        {canCorrectCar && (
          <Card data-testid="car-correction-card" data-jump="Change booking">
            <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
              Correct Car / Location
            </h2>
            <p className="text-sm text-steel mb-2">
              Fix a customer's booking mistake — wrong car or wrong pickup location, paid or not.
              The price stays exactly as it is; the replacement car may be at a different location.
            </p>
            {booking.car && (
              <p className="text-sm text-steel mb-2">
                Current: {booking.car.label} — {booking.car.plate_number}
              </p>
            )}
            <label className="flex flex-col gap-1 text-sm" htmlFor="correction-car-plate">
              <span className="text-steel">Replacement car (may be a different location)</span>
              <Input
                id="correction-car-plate"
                type="text"
                placeholder="Search by plate number"
                value={correctionPlateInput}
                onChange={(e) => {
                  setCorrectionPlateInput((e.target as HTMLInputElement).value);
                  setCorrectionCarId('');
                }}
              />
            </label>
            {correctionCarOptions?.items &&
              correctionCarOptions.items.length > 0 &&
              !correctionCarId && (
                <div className="grid gap-1 mt-2">
                  {correctionCarOptions.items.map((c) => (
                    <button
                      key={c._id}
                      type="button"
                      className="text-left text-sm px-3 py-2 rounded border border-hairline hover:bg-surface-alt"
                      onClick={() => {
                        setCorrectionCarId(c._id);
                        setCorrectionPlateInput(c.plate_number);
                      }}
                    >
                      {c.name} — {c.plate_number}
                    </button>
                  ))}
                </div>
              )}
            <label className="flex flex-col gap-1 text-sm mt-3" htmlFor="correction-reason">
              <span className="text-steel">Why is this being corrected?</span>
              <Textarea
                id="correction-reason"
                rows={2}
                value={correctionReason}
                onChange={(e) => setCorrectionReason(e.target.value)}
              />
            </label>
            {/* 386 (#1278, FR-011): the gap on the corrected car — a warning only. */}
            {correctionCarId && booking && (
              <div className="mt-2">
                <GapWarning
                  carId={correctionCarId}
                  pickupAt={booking.pickup_at}
                  returnAt={booking.return_at}
                  bookingId={booking.id ?? id}
                />
              </div>
            )}
            {correctionError && (
              <>
                <p className="mt-3 text-sm text-semantic-danger-text">{correctionError}</p>
                <AlternativeCars
                  items={correctionAlternatives}
                  onSelect={(car) => {
                    setCorrectionCarId(car.id);
                    setCorrectionPlateInput(car.name);
                  }}
                />
              </>
            )}
            <div className="mt-3">
              <Button
                onClick={() => {
                  setCorrectionError(null);
                  void correctCarAssignment.save({
                    car_id: correctionCarId,
                    reason: correctionReason.trim(),
                  });
                }}
                disabled={
                  !correctionCarId || !correctionReason.trim() || correctCarAssignment.isPending
                }
              >
                Confirm correction
              </Button>
            </div>
            <CustomerToldNote />
          </Card>
        )}

        <Card data-jump="Change booking">
          <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
            Price Override
          </h2>
          {/* 189 (D2/FR-019): what the proposed window would cost, beside what is recorded. Shown
              only when a real quote came back — never as RM0.00 for a window nothing can price. */}
          {/* 190: the difference is split, because one number was answering two questions.
              🔴 Found on staging: `recorded 30000 / proposed 30599` for a window that had NOT moved,
              because the booking was priced before 143's proration existed. Both numbers honest; the
              framing was not — "+RM5.99" beside a window being edited reads as caused by the edit.
              What leads is now the part the operator actually caused. */}
          {quoted && (
            <div data-testid="price-preview" className="mb-4 text-sm text-ink">
              <p>
                Recorded {formatMyr(quoted.current.total_minor)} · this window would be{' '}
                <span className="font-semibold">{formatMyr(quoted.proposed.total_minor)}</span>
              </p>
              <p data-testid="price-move" className="mt-1">
                {quoted.move_difference_minor === 0 ? (
                  <span className="text-steel">Your change: no change to the price</span>
                ) : (
                  <>
                    Your change:{' '}
                    <span className="font-semibold">
                      {quoted.move_difference_minor >= 0 ? '+' : '−'}
                      {formatMyr(Math.abs(quoted.move_difference_minor))}
                    </span>
                  </>
                )}
              </p>
              {/* Only when there IS drift — a healthy booking gets no extra line to read past. */}
              {quoted.repricing_drift_minor !== 0 && (
                <p data-testid="price-drift" className="mt-1 text-steel">
                  Separately, {formatMyr(Math.abs(quoted.repricing_drift_minor))}{' '}
                  {quoted.repricing_drift_minor > 0 ? 'more' : 'less'} than recorded because pricing
                  has changed since this booking was priced — not caused by your change.
                </p>
              )}
            </div>
          )}

          {priceBlockedReason && (
            <p data-testid="price-blocked" className="mb-4 text-sm text-steel">
              {PRICE_BLOCKED[priceBlockedReason]}
            </p>
          )}

          <dl className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-2 text-sm mb-4">
            <dt className="text-steel">Computed total</dt>
            <dd>
              {formatMyr(
                booking.pricing_snapshot?.totals?.total_minor ?? booking.total_price_minor,
              )}
            </dd>
            {booking.pricing_snapshot?.totals?.override_total_minor !== undefined && (
              <>
                <dt className="text-steel">Active override</dt>
                <dd className="font-semibold">
                  {formatMyr(booking.pricing_snapshot.totals.override_total_minor)} —{' '}
                  {booking.pricing_snapshot.totals.override_reason}
                </dd>
              </>
            )}
          </dl>
          <div className="flex flex-col gap-3 max-w-sm">
            <label className="flex flex-col gap-1 text-sm" htmlFor="override-amount">
              Override total (RM)
              <Input
                id="override-amount"
                type="number"
                step="0.01"
                min="0"
                value={overrideAmount}
                onChange={(e) => setOverrideAmount(e.target.value)}
                disabled={priceLocked}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm" htmlFor="override-reason">
              Reason
              <Textarea
                id="override-reason"
                rows={2}
                value={overrideReason}
                onChange={(e) => setOverrideReason(e.target.value)}
                disabled={priceLocked}
              />
            </label>
            <div className="flex gap-2">
              <Button
                size="sm"
                loading={setOverride.isPending}
                disabled={priceLocked || !overrideAmount || !overrideReason.trim()}
                onClick={() => setOverride.mutate()}
              >
                Set Override
              </Button>
              {booking.pricing_snapshot?.totals?.override_total_minor !== undefined && (
                <Button
                  size="sm"
                  variant="secondary"
                  loading={clearOverride.isPending}
                  disabled={priceLocked}
                  onClick={() => clearOverride.mutate()}
                >
                  Clear Override
                </Button>
              )}
            </div>
            {setOverride.isError && (
              <p className="text-sm text-red-500">
                {(setOverride.error as { message?: string }).message ?? 'Failed to set override'}
              </p>
            )}
          </div>
        </Card>

        {/* 300 (#403): refunds happen OUTSIDE this platform (bank transfer, etc.) — this box only
            LOGS one, for record-keeping, via the already-existing ledger-only refund route. */}
        {canSeeRefunds && (
          <Card data-testid="refund-log-card" data-jump="Payments">
            <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
              {canLogRefund ? 'Log a refund' : 'Refunds'}
            </h2>
            <p className="text-sm text-steel mb-2">
              Refunds are paid outside this system (bank transfer, etc.). This only records that one
              happened — it never contacts Bayarcash or any payment gateway.
            </p>
            {refunds && refunds.length > 0 && (
              <ul className="mb-3 space-y-1 text-sm">
                {refunds.map((r) => (
                  <li key={r.id} className="flex flex-wrap gap-x-3">
                    <span className="font-medium">{formatMyr(r.amount_minor)}</span>
                    <span className="text-steel capitalize">{r.status}</span>
                    <span className="text-muted text-xs">{formatDate(r.created_at)}</span>
                    {r.reason && <span className="w-full text-steel">{r.reason}</span>}
                  </li>
                ))}
              </ul>
            )}
            {canLogRefund && (
              <div className="flex flex-col gap-3 max-w-sm">
                <label className="flex flex-col gap-1 text-sm" htmlFor="refund-amount">
                  Refund amount (RM)
                  <Input
                    id="refund-amount"
                    type="number"
                    step="0.01"
                    min="0"
                    value={refundAmount}
                    onChange={(e) => setRefundAmount((e.target as HTMLInputElement).value)}
                  />
                </label>
                <label className="flex flex-col gap-1 text-sm" htmlFor="refund-reason">
                  Reason
                  <Textarea
                    id="refund-reason"
                    rows={2}
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                  />
                </label>
                {refundError && <p className="text-sm text-semantic-danger-text">{refundError}</p>}
                <div>
                  <Button
                    size="sm"
                    loading={logRefund.isPending}
                    disabled={!refundAmount || !refundReason.trim() || logRefund.isPending}
                    onClick={() => {
                      setRefundError(null);
                      logRefund.mutate();
                    }}
                  >
                    Log refund
                  </Button>
                </div>
              </div>
            )}
          </Card>
        )}

        {/* 407: how late a rental still out is, the car's next booking, and how it ended. */}
        {id && <LateStatusPanel bookingId={id} />}

        {/* 416 (#1431, mock-up screens 4 + 8): the geo-fence figures and the route, once the platform has looked. */}
        {id && booking.zone_mileage && (
          <ZoneDistanceCard bookingId={id} zone={booking.zone_mileage} />
        )}

        {/* 417 (#1464, mock-up frame 2): the mileage package and, once the rental ends, the km charge to decide. */}
        {id && booking.mileage_package && (
          <KmChargeCard
            bookingId={id}
            pkg={booking.mileage_package}
            charge={booking.mileage_charge ?? null}
            canDecide={penaltyPowers(ability).create}
          />
        )}

        {(transitions.length > 0 || !TERMINAL_STATUSES.has(booking.status)) && (
          <Card data-jump="Status">
            <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
              Advance Status
            </h2>
            {ENDABLE_STATUSES.has(booking.status) &&
              ability.can('checkin', 'Booking') &&
              ability.can('complete', 'Booking') && (
                <div className="flex gap-2 flex-wrap">
                  <Button size="sm" variant="primary" onClick={() => setConfirmEnd(true)}>
                    End booking
                  </Button>
                </div>
              )}
            <Modal
              open={confirmEnd}
              onClose={() => setConfirmEnd(false)}
              title="End this booking?"
              footer={
                <>
                  <Button size="sm" variant="secondary" onClick={() => setConfirmEnd(false)}>
                    Cancel
                  </Button>
                  <Button
                    size="sm"
                    variant="primary"
                    loading={endRental.isPending}
                    onClick={() => endRental.mutate()}
                  >
                    End booking
                  </Button>
                </>
              }
            >
              <p className="text-sm text-steel">
                The rental is finished now, even without return photos. It is recorded as ended by
                staff. Add any late penalty from Penalties.
              </p>
              {endRental.isError && (
                <p className="mt-2 text-sm text-semantic-danger-text">
                  {(endRental.error as { message?: string })?.message ||
                    'The booking could not be ended.'}
                </p>
              )}
            </Modal>
            {transitions.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {transitions.map((t) => (
                  <Button
                    key={t}
                    size="sm"
                    variant="primary"
                    loading={updateStatus.isPending && selectedStatus === t}
                    onClick={() => {
                      setSelectedStatus(t);
                      updateStatus.mutate({ status: t });
                    }}
                  >
                    {t.replace(/_/g, ' ')}
                  </Button>
                ))}
              </div>
            )}
            {/* 300 (#403): available from EVERY non-terminal status, not just pending/confirmed —
                the backend already permits it (advanceStatus's own terminal guard is the only
                refusal); this control is the gap this feature closes. */}
            {!TERMINAL_STATUSES.has(booking.status) && !isPast && (
              <div className="mt-3 flex flex-col gap-2 max-w-sm">
                <label className="flex flex-col gap-1 text-sm" htmlFor="cancel-reason">
                  Why cancel this booking?
                  <Textarea
                    id="cancel-reason"
                    rows={2}
                    value={cancelReason}
                    onChange={(e) => setCancelReason(e.target.value)}
                  />
                </label>
                <Button
                  size="sm"
                  variant="secondary"
                  disabled={
                    !cancelReason.trim() ||
                    (updateStatus.isPending && selectedStatus !== 'cancelled')
                  }
                  loading={updateStatus.isPending && selectedStatus === 'cancelled'}
                  onClick={() => {
                    setSelectedStatus('cancelled');
                    updateStatus.mutate({ status: 'cancelled', reason: cancelReason.trim() });
                  }}
                >
                  Cancel booking
                </Button>
              </div>
            )}
            {updateStatus.isError && (
              <p className="mt-2 text-sm text-red-500">
                {(updateStatus.error as { message?: string }).message ?? 'Failed to update status'}
              </p>
            )}
          </Card>
        )}

        {/* 410 (#1472, mock-up frames 1–3): a past rental. The platform says who may cancel it; the page never decides. */}
        {isPast && booking.status !== 'cancelled' && booking.status !== 'expired' && (
          <Card data-jump="Cancel">
            {booking.past_cancel?.allowed ? (
              <div data-testid="past-cancel-card" className="flex flex-col gap-3">
                <h2 className="text-sm font-semibold text-steel uppercase tracking-wide">
                  Cancel this past booking
                </h2>
                <p className="text-sm text-semantic-danger-text">
                  This rental has ended. Cancelling it removes the whole rental from the sales
                  figures. The customer is not told. Credit and the voucher code go back to the
                  customer; refund any payment by hand from Payments.
                </p>
                <label
                  className="flex flex-col gap-1 text-sm max-w-sm"
                  htmlFor="past-cancel-reason"
                >
                  Why cancel this past booking?
                  <Textarea
                    id="past-cancel-reason"
                    rows={2}
                    value={pastCancelReason}
                    onChange={(e) => setPastCancelReason(e.target.value)}
                  />
                </label>
                <div>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={!pastCancelReason.trim()}
                    onClick={() => setConfirmPastCancel(true)}
                  >
                    Cancel past booking…
                  </Button>
                </div>
                {pastCancel.isError && !confirmPastCancel && (
                  <p data-testid="past-cancel-error" className="text-sm text-semantic-danger-text">
                    {(pastCancel.error as { message?: string })?.message ||
                      'The booking could not be cancelled.'}
                  </p>
                )}
                <Modal
                  open={confirmPastCancel}
                  onClose={() => setConfirmPastCancel(false)}
                  title="Cancel this past rental?"
                  footer={
                    <>
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => setConfirmPastCancel(false)}
                      >
                        Keep it
                      </Button>
                      <Button
                        size="sm"
                        variant="primary"
                        loading={pastCancel.isPending}
                        onClick={() => pastCancel.mutate(pastCancelReason.trim())}
                      >
                        Cancel past rental
                      </Button>
                    </>
                  }
                >
                  <ul
                    data-testid="past-cancel-confirm"
                    className="text-sm text-steel list-disc pl-5 flex flex-col gap-1"
                  >
                    <li>
                      {pastLegCount === 1
                        ? 'This booking is cancelled'
                        : `All ${pastLegCount} bookings of this rental are cancelled`}{' '}
                      and leave the sales figures.
                    </li>
                    <li>The customer is not told.</li>
                    <li>
                      {pastCredit > 0
                        ? `${formatMyr(pastCredit)} of credit and the voucher code, if any, go back to the customer.`
                        : 'The voucher code, if any, goes back to the customer.'}
                    </li>
                    <li>
                      Anything paid stays paid: refund it by hand from Payments if it is owed.
                    </li>
                  </ul>
                  {pastCancel.isError && (
                    <p
                      data-testid="past-cancel-error"
                      className="mt-2 text-sm text-semantic-danger-text"
                    >
                      {(pastCancel.error as { message?: string })?.message ||
                        'The booking could not be cancelled.'}
                    </p>
                  )}
                </Modal>
              </div>
            ) : (
              <p data-testid="past-cancel-refused" className="text-sm text-steel">
                {PAST_CANCEL_REFUSED}
              </p>
            )}
          </Card>
        )}

        {/* 311 (#549, US2): a booking cancelled while its car was out keeps the hold, so the car
            cannot be rebooked while a customer may still have it. This is where staff release it
            once the car is physically back. The notice shows to anyone who can read it; the action
            only to those who may check the booking in — the ability the backend enforces. */}
        {/* 387 (#1308, FR-007, mock-up screens 6–8): the customer's lockbox code waits for their pickup photos; staff
            who may hand the car over can release it. */}
        {(booking.lockbox_release ||
          booking.lockbox_gate === 'await_photos' ||
          booking.lockbox_gate === 'ask_staff') && (
          <Card data-testid="lockbox-release-card" data-jump="Status">
            <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
              Lockbox code
            </h2>
            <dl className="grid grid-cols-[130px_1fr] gap-x-4 gap-y-2 text-sm">
              {booking.car?.lockbox_code && (
                <>
                  <dt className="text-steel">Lockbox code</dt>
                  <dd className="font-mono font-semibold tracking-widest">
                    {booking.car.lockbox_code}
                  </dd>
                </>
              )}
              <dt className="text-steel">Customer sees it</dt>
              <dd>
                {booking.lockbox_release ? (
                  <div className="rounded-md bg-semantic-success-bg px-3 py-2 text-semantic-success-text">
                    Lockbox code released by {booking.lockbox_release.by_name ?? 'a staff member'}
                    <span className="block text-xs text-steel">
                      {formatReleaseTime(booking.lockbox_release.at)}
                      {booking.lockbox_release.reason ? ` · ${booking.lockbox_release.reason}` : ''}
                    </span>
                  </div>
                ) : (
                  <>
                    <div className="rounded-md bg-semantic-warning-bg px-3 py-2 text-semantic-warning-text">
                      Pickup photos not sent — the customer cannot see the lockbox code.
                    </div>
                    {ability.can('checkout', 'Booking') && (
                      <div className="mt-3">
                        <Button size="sm" variant="secondary" onClick={() => setReleaseOpen(true)}>
                          Release lockbox code
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </dd>
            </dl>
            {releaseLockbox.isError && (
              <p className="mt-2 text-sm text-red-500">
                {(releaseLockbox.error as { message?: string }).message ??
                  'The lockbox code could not be released'}
              </p>
            )}
          </Card>
        )}
        <Modal
          open={releaseOpen}
          onClose={() => setReleaseOpen(false)}
          title="Release the lockbox code?"
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setReleaseOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                variant="primary"
                loading={releaseLockbox.isPending}
                disabled={releaseReason.trim().length > LOCKBOX_REASON_MAX}
                onClick={() => releaseLockbox.mutate()}
              >
                Release code
              </Button>
            </div>
          }
        >
          <p className="text-sm text-steel mb-3">
            The customer will see the code now, without pickup photos. This is recorded in the
            booking's history.
          </p>
          <Textarea
            rows={2}
            value={releaseReason}
            onChange={(e) => setReleaseReason(e.target.value)}
            placeholder="Reason (optional), e.g. phone camera not working"
            aria-label="Reason (optional)"
          />
          <p className="text-xs text-steel text-right mt-1">
            {releaseReason.trim().length} / {LOCKBOX_REASON_MAX}
          </p>
        </Modal>
        {booking.holds_car && (
          <Card data-testid="vehicle-hold-card" data-jump="Status">
            <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
              Car still held
            </h2>
            <p className="text-sm text-ink">
              This cancelled booking still holds its car. It was cancelled while the car was out, so
              the car stays blocked for the booking's dates until someone confirms it is back.
            </p>
            {ability.can('checkin', 'Booking') && (
              <div className="mt-3">
                <Button size="sm" variant="primary" onClick={() => setConfirmReturnOpen(true)}>
                  Vehicle returned — release hold
                </Button>
              </div>
            )}
            {markReturned.isError && (
              <p className="mt-2 text-sm text-red-500">
                {(markReturned.error as { message?: string }).message ??
                  'Failed to release the hold'}
              </p>
            )}
          </Card>
        )}
        <Modal
          open={confirmReturnOpen}
          onClose={() => setConfirmReturnOpen(false)}
          title="Release the hold?"
          size="sm"
          footer={
            <div className="flex justify-end gap-2">
              <Button size="sm" variant="secondary" onClick={() => setConfirmReturnOpen(false)}>
                Keep hold
              </Button>
              <Button
                size="sm"
                variant="primary"
                loading={markReturned.isPending}
                onClick={() => markReturned.mutate()}
              >
                Release hold
              </Button>
            </div>
          }
        >
          <p className="text-sm">
            Confirm the car is physically back. It becomes bookable again for the rest of this
            booking's dates, and the booking stays cancelled. This cannot be undone here.
          </p>
        </Modal>

        <Card data-jump="Timeline">
          <h2 className="text-sm font-semibold text-steel uppercase tracking-wide mb-3">
            Event Timeline
          </h2>
          {!eventsData?.items.length && <p className="text-sm text-muted">No events yet.</p>}
          <ol className="space-y-3">
            {eventsData?.items.map((ev) => (
              <li key={ev.id} className="flex gap-3 text-sm">
                <span className="text-muted select-none">•</span>
                <div>
                  <span className="font-medium capitalize">{ev.event_type.replace(/_/g, ' ')}</span>
                  {ev.metadata?.past_cancel === true && (
                    <span
                      data-testid="event-past-cancel"
                      className="ml-1 text-semantic-danger-text"
                    >
                      · Past cancellation
                    </span>
                  )}
                  {lateEventDetails(ev) && (
                    <p data-testid="event-late-details" className="text-steel mt-0.5">
                      {lateEventDetails(ev)}
                    </p>
                  )}
                  {ev.from_status && ev.to_status && (
                    <span className="text-muted ml-1">
                      {ev.from_status} → {ev.to_status}
                    </span>
                  )}
                  <span className="ml-2 text-muted text-xs">{formatDate(ev.created_at)}</span>
                  {ev.notes && <p className="text-steel mt-0.5">{ev.notes}</p>}
                </div>
              </li>
            ))}
          </ol>
        </Card>
      </div>
    </div>
  );
}

const LATE_METHOD: Record<string, string> = {
  cash: 'Cash',
  bank_transfer: 'Bank transfer',
  qr: 'QR',
  other: 'Other',
};
const lateWhen = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Asia/Kuala_Lumpur',
  day: 'numeric',
  month: 'short',
  hour: 'numeric',
  minute: '2-digit',
  hour12: true,
});
const lateRm = (v: unknown) => `RM ${(Number(v ?? 0) / 100).toFixed(2)}`;

/** 421 (#1528, mock-up frame 3): one line saying what a late extension event recorded or decided. */
function lateEventDetails(ev: { event_type: string; metadata?: Record<string, unknown> }) {
  const m = ev.metadata ?? {};
  if (ev.event_type === 'late_extension_recorded') {
    const until = m.return_at
      ? `Until ${lateWhen.format(new Date(String(m.return_at))).replace(/\s?(am|pm)$/i, ' $1')}`
      : 'Late extension';
    if (m.payment === 'qr') return `${until} · the customer pays by QR ${lateRm(m.amount_minor)}`;
    const differs =
      m.computed_minor !== undefined && m.computed_minor !== m.amount_minor
        ? ` (rate card ${lateRm(m.computed_minor)})`
        : '';
    return [
      `${until} · paid outside the app ${lateRm(m.amount_minor)}${differs}`,
      LATE_METHOD[String(m.method)] ?? m.method,
      m.reference,
    ]
      .filter(Boolean)
      .join(' · ');
  }
  if (ev.event_type === 'late_extension_payment_approved') {
    return `By QR · ${lateRm(m.amount_minor)}`;
  }
  if (ev.event_type === 'late_extension_payment_rejected') {
    return `Reason: ${String(m.reason ?? '')}`;
  }
  return null;
}

/** 390 (#1310, mock-up screen 4): the customer's receipt, for staff to send on request. */
function ReceiptButton({ bookingId, rentalId }: { bookingId: string; rentalId: string }) {
  const [state, setState] = useState<'idle' | 'saving' | 'failed'>('idle');
  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        size="sm"
        variant="secondary"
        disabled={state === 'saving'}
        onClick={async () => {
          setState('saving');
          try {
            await downloadReceipt(bookingId, rentalId);
            setState('idle');
          } catch {
            setState('failed');
          }
        }}
      >
        Receipt (PDF)
      </Button>
      {state === 'failed' && (
        <p role="alert" className="text-xs text-semantic-danger-text">
          The receipt could not be downloaded.
        </p>
      )}
    </div>
  );
}
